import React, { useState, useMemo } from 'react';
import { arrowDownIcon, plusIcon, searchIcon } from '../../utils/images';
import { ProductCard } from '../ProductCard';
import { Pagination } from '../Pagination';
import { ProductWithTagNames } from '../../types/Product';
import './ProductsList.scss';
import { ImageAI } from '../ImageAI';

interface ProductsListProps {
  products: ProductWithTagNames[];
  onGetAuthenticatedImage?: (imageUrl: string) => Promise<string>;
  onProductUpdate?: (productId: number) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
}

export const ProductsList: React.FC<ProductsListProps> = ({ 
  products, 
  onGetAuthenticatedImage,
  onProductUpdate,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  loading = false
}) => {
  const [selectedType, setSelectedType] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [showImageAI, setShowImageAI] = useState<boolean>(false);

  // Extract unique types from AI tags (now these are tag names, not IDs)
  const uniqueTypes = useMemo(() => {
    const allTags = new Set<string>();
    products.forEach(product => {
      product.ai_tags.forEach(tag => allTags.add(tag));
    });
    return ['All', ...Array.from(allTags)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const typeMatch = selectedType === 'All' || product.ai_tags.includes(selectedType);
      const query = searchQuery.toLowerCase();
      const nameMatch = product.name.toLowerCase().includes(query);
      // const descriptionMatch = product.description.toLowerCase().includes(query);
      // const tagMatch = product.ai_tags.some(tag => tag.toLowerCase().includes(query));
      return typeMatch && (nameMatch);
    });
  }, [products, selectedType, searchQuery]);

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    setIsDropdownOpen(false);
  };

  const handleAddProduct = () => {
    setShowImageAI(true);
  };

  const handleCloseImageAI = () => {
    setShowImageAI(false);
  };

  return (
    <>
      <div className="products-list">
        <div className="products-list__title">
          <label className="products-list__label">
          <p className="products-list__label--text">Products</p>
          </label>
          <button className="products-list__add-button" onClick={handleAddProduct}>
            <img 
              className="products-list__add-button-icon products-list__add-button-icon--left"
              src={plusIcon} 
              alt='plus icon'
            />
            <span className="products-list__add-button-text">Add Product</span>
          </button>
        </div>

        <div className="products-list__actions">
          <div className="products-list__filters">
            <div className="products-list__dropdown-container">
              <div 
                className="products-list__dropdown" 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <p className="products-list__dropdown--text">{selectedType === 'All' ? 'All' : selectedType}</p>
                <img 
                  className={`products-list__dropdown--icon ${isDropdownOpen ? 'open' : ''}`}
                  src={arrowDownIcon} 
                  alt="dropdown icon" 
                />
              </div>
              {isDropdownOpen && (
                <div className="products-list__dropdown-menu">
                  {uniqueTypes.map(type => (
                    <div 
                      key={type} 
                      className={`products-list__dropdown-item ${selectedType === type ? 'selected' : ''}`}
                      onClick={() => handleTypeSelect(type)}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="products-list__search">
              <img className="products-list__search--icon" src={searchIcon} alt='search icon'/>
              <input 
                type="text" 
                className="products-list__search--input" 
                placeholder="Search by name, description or #tag" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="products-list__list">
          {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onGetAuthenticatedImage={onGetAuthenticatedImage}
                  onProductUpdate={onProductUpdate}
                />
              ))
          ) : (
            <p className="products-list__no-results">No products found.</p>
          )}
        </div>

        {onPageChange && (
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            disabled={loading}
          />
        )}
      </div>

      {showImageAI && (
        <div className="products-list__popup-overlay">
          <ImageAI onClose={handleCloseImageAI} />
        </div>
      )}
    </>
  );
};